import { useCallback, useEffect, useRef } from "react";

/* Only the top-most surface reacts to Escape, so dismissing a confirm stacked
   over a modal does not tear down the modal underneath it at the same time. */
const escapeStack = [];

/**
 * Dismiss behaviour for the overlay/modal pairs used across the app.
 *
 * Escape always closes. Backdrop clicks close only when `dismissOnBackdrop` is
 * set — modals holding a form leave it off, so a stray click on the overlay
 * cannot throw away whatever the user had typed.
 *
 * Where the backdrop does dismiss, the press and the release must both land on
 * the overlay. A drag that starts inside the modal and ends outside it (picking
 * a date, selecting text, pulling a crop handle) reports the overlay as the
 * click target, which would otherwise read as a click on the backdrop.
 *
 * Spread the returned props onto the overlay element.
 */
const useModalDismiss = (onClose, { dismissOnBackdrop = false, enabled = true } = {}) => {
    const closeRef = useRef(onClose);
    const pressedOnOverlay = useRef(false);
    const releasedOnOverlay = useRef(false);

    useEffect(() => {
        closeRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        if (!enabled) return undefined;

        const token = {};
        escapeStack.push(token);

        const onKeyDown = (e) => {
            if (e.key !== "Escape") return;
            if (escapeStack[escapeStack.length - 1] !== token) return;
            closeRef.current?.();
        };

        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            const at = escapeStack.indexOf(token);
            if (at !== -1) escapeStack.splice(at, 1);
        };
    }, [enabled]);

    const onMouseDown = useCallback((e) => {
        pressedOnOverlay.current = e.target === e.currentTarget;
    }, []);

    const onMouseUp = useCallback((e) => {
        releasedOnOverlay.current = e.target === e.currentTarget;
    }, []);

    const onClick = useCallback((e) => {
        if (e.target !== e.currentTarget) return;
        if (!pressedOnOverlay.current || !releasedOnOverlay.current) return;
        pressedOnOverlay.current = false;
        releasedOnOverlay.current = false;
        closeRef.current?.();
    }, []);

    if (!enabled || !dismissOnBackdrop) return {};
    return { onMouseDown, onMouseUp, onClick };
};

export default useModalDismiss;
